/**
 * A Health figure on screen — its value in its unit, the line under it, its (i), and the colour a
 * management target gives it (bata-reqs docs/25 §5.6, O5; bata-platform ADR 9053/9054; `docs/adr/3003`).
 *
 * - **Value.** A share is a percentage, weeks have one decimal, an amount is money in its currency, every
 *   other unit is a whole count. A total — cells, units, alerts, steps, an amount — is always a number: what
 *   is known, nought when nothing is (owner rule 2026-10-14); how much is known goes in the (i). A share or a
 *   median with no base is absent, in words (ADR 9061), never «—» or 0.
 * - **Colour.** With a target in force the platform says `green`, `amber` or `red`, and the figure carries a
 *   status with its icon and its words (§12.6: never colour alone). With none the figure is not coloured, as
 *   before (O5's fallback).
 * - **(i).** What the figure is, how much of it is known, the target with its scope and change set (or that
 *   none is in force), and what it was read from. The scope is the registry row's (bata-platform ADR 9055):
 *   the market's, or one department family's, which wins over the market's where both are in force.
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { HealthFigureModel, HealthReportModel } from '@/api/generated';
import { formatCurrency, formatDate, formatInteger, formatNumber, formatPercent } from '@/lib/format';
import { AboutPopover, DeltaValue, KpiLinkAbove, KpiLinkAnchor, StatusIndicator, useKpiLink } from '@/primitives';

import {
  absentReasonKey, isFullPriceVerdict, measureWords, polarityOf, provenanceText, ragOf, shownValue, type HealthReportId,
} from './reports';

/** A figure's value in its unit, in the reader's locale; `null` when it is absent. Pure. */
export function formatFigure(
  figure: Pick<HealthFigureModel, 'value' | 'unit' | 'currencyCode'> & { readonly absentReason?: string | null },
  locale: string,
): string | null {
  const value = shownValue(figure);
  if (value === null) return null;
  switch (figure.unit) {
    case 'share': return formatPercent(value, locale, 1);
    case 'weeks': return formatNumber(value, locale, { fractionDigits: 1 });
    case 'amount': return figure.currencyCode === null ? formatNumber(value, locale, { fractionDigits: 2 }) : formatCurrency(value, figure.currencyCode, locale);
    default: return formatInteger(Math.round(value), locale);
  }
}

/** A figure that is absent, in words: why. */
export function absentWords(t: TFunction, figure: Pick<HealthFigureModel, 'absentReason'>): string {
  return t(absentReasonKey(figure.absentReason), { code: figure.absentReason ?? '' });
}

/** The (i)'s values for one figure: coverage, target, source. Pure over the read. */
export function figureHelpValues(
  figure: HealthFigureModel,
  data: Pick<HealthReportModel, 'countryKey' | 'asOf' | 'sourceId' | 'periodKey' | 'provenance'>,
  locale: string,
): Readonly<Record<string, string | number>> {
  const n = (value: number | null | undefined): string => (value === null || value === undefined ? '' : formatInteger(value, locale));
  const target = figure.target;
  const breakdown = figure.breakdown;
  return {
    market: data.countryKey,
    asOf: data.asOf === null ? '' : formatDate(data.asOf, locale, 'UTC'),
    source: data.sourceId ?? '',
    period: data.periodKey ?? '',
    known: n(figure.known),
    total: n(figure.total),
    uncovered: n(figure.uncovered),
    target: target === null ? '' : formatFigure({ value: target.value, unit: figure.unit, currencyCode: figure.currencyCode }, locale) ?? '',
    tolerance: target?.amberTolerance === null || target?.amberTolerance === undefined ? '' : formatPercent(target.amberTolerance, locale, 1),
    changeSet: target?.changeId ?? '',
    toleranceChangeSet: target?.toleranceChangeId ?? '',
    validFrom: target?.validFrom === null || target?.validFrom === undefined ? '' : formatDate(target.validFrom, locale, 'UTC'),
    family: targetFamily(target) ?? '',
    articles: n(breakdown['articles']),
    fullPriceArticles: n(breakdown['full_price_articles']),
    phaseUnknown: n(breakdown['phase_unknown']),
    endOfLife: n(breakdown['end_of_life_or_outlet_phase']),
    majority: n(breakdown['markdown_in_force_at_majority']),
    kept: n(breakdown['partial_markdown_kept']),
    rangedStores: n(breakdown['rangedStores']),
    inForceStores: n(breakdown['inForceStores']),
    nonSelling: n(breakdown['nonSelling']),
    unmeasurable: n(breakdown['unmeasurable']),
    uncostedUnits: n(rounded(breakdown['uncostedUnits'])),
    rangeRun: provenanceText(data, 'fullPriceRangeRunId') ?? '',
    // Markdown exposure at Zona sale entry (bata-platform ADR 9053 amended 2026-11-03).
    mainArticles: n(breakdown['mainArticles']),
    mainOnHand: n(breakdown['mainOnHandUnits']),
    salesToEntry: n(rounded(breakdown['forecastSalesToEntryUnits'])),
    dueNow: n(breakdown['dueNowArticles']),
    unforecast: n(breakdown['unforecastArticles']),
    unforecastUnits: n(breakdown['unforecastOnHandUnits']),
    entryUnknown: n(breakdown['entryUnknownArticles']),
    entryUnknownUnits: n(breakdown['entryUnknownOnHandUnits']),
    beyondHorizon: n(breakdown['forecastBeyondHorizonArticles']),
    horizonDays: provenanceText(data, 'forecastHorizonDays') ?? '',
    zonaMonth: provenanceText(data, 'zonaEntryMonthOfLife') ?? '',
    atCost: atCostWords(breakdown['atCost'], figure.currencyCode, locale),
  };
}

/** A fractional count of pairs (a forecast) as a whole one for the words; `null` stays `null`. Pure. */
function rounded(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.round(value);
}

/** A stock figure's landed cost in words, or `''` when it has none (two currencies meet). Pure. */
function atCostWords(value: number | null | undefined, currency: string | null, locale: string): string {
  if (value === null || value === undefined) return '';
  return currency === null ? formatNumber(value, locale, { fractionDigits: 2 }) : formatCurrency(value, currency, locale);
}

/** Markdown exposure's measures (bata-platform ADR 9053 amended 2026-11-03). */
export const ZONA_STOCK_MEASURES: readonly string[] = ['zona_sales_stock_units', 'outlet_stock_units'];

/**
 * The line under a Markdown exposure KPI, as a key and its values, or `null`: how many main-line
 * articles the pairs sum (known of total), how many pairs the cost leaves uncosted, and what the stock
 * already in the Zona sale or the outlet is worth. Pure.
 */
export function markdownKpiContext(
  figure: Pick<HealthFigureModel, 'measure' | 'known' | 'total' | 'breakdown' | 'currencyCode'>,
  locale: string,
): { readonly key: string; readonly values: Readonly<Record<string, string | number>> } | null {
  const n = (value: number): string => formatInteger(Math.round(value), locale);
  if (figure.measure === 'markdown_exposure_units' && figure.known !== null && figure.total !== null) {
    return { key: 'strategic.health.kpi.knownArticles', values: { known: n(figure.known), total: n(figure.total) } };
  }
  if (figure.measure === 'markdown_exposure_at_cost') {
    const uncosted = figure.breakdown['uncostedUnits'] ?? null;
    return uncosted === null || uncosted <= 0 ? null : { key: 'strategic.health.kpi.uncostedPairs', values: { count: Math.round(uncosted), n: n(uncosted) } };
  }
  if (ZONA_STOCK_MEASURES.includes(figure.measure)) {
    const articles = figure.breakdown['articles'] ?? 0;
    const cost = atCostWords(figure.breakdown['atCost'], figure.currencyCode, locale);
    return cost === ''
      ? { key: 'strategic.health.kpi.stockArticles', values: { count: articles, n: n(articles) } }
      : { key: 'strategic.health.kpi.stockArticlesAtCost', values: { count: articles, n: n(articles), cost } };
  }
  return null;
}

/** The department family a target was stated for, or `null` for the market's own row (bata-platform ADR 9055). */
export function targetFamily(target: HealthFigureModel['target']): string | null {
  if (target === null) return null;
  return target.departmentFamily ?? target.scope.find((pair) => pair.dimension === 'department_family')?.value ?? null;
}

/** The (i)'s target paragraph: none in force, the market's, or a family's — each with or without an amber band. */
export function targetHelpKey(target: HealthFigureModel['target']): string {
  if (target === null) return 'strategic.health.help.noTarget';
  const family = targetFamily(target) !== null;
  if (target.amberTolerance === null) return family ? 'strategic.health.help.targetFamilyNoBand' : 'strategic.health.help.targetNoBand';
  return family ? 'strategic.health.help.targetFamily' : 'strategic.health.help.target';
}

/** The (i)'s paragraphs for one figure, in order: what it is, its parts, how much is known, its target. */
export function figureHelpKeys(figure: HealthFigureModel, report: HealthReportId): readonly string[] {
  const keys = [measureWords(figure.measure).helpKey];
  if (figure.measure === 'full_price_availability' && 'articles' in figure.breakdown) keys.push('strategic.health.help.fullPriceSubset');
  if (isFullPriceVerdict(figure)) keys.push('strategic.health.help.fullPriceVerdict');
  if (figure.measure === 'aged_cover_units' && 'unmeasurable' in figure.breakdown) keys.push('strategic.health.help.agedParts');
  if (figure.measure === 'markdown_exposure_units' && 'mainArticles' in figure.breakdown) {
    keys.push('strategic.health.help.zonaParts');
    if ((figure.breakdown['forecastBeyondHorizonArticles'] ?? 0) > 0) keys.push('strategic.health.help.zonaHorizon');
  }
  if (figure.measure === 'markdown_exposure_at_cost' && 'uncostedUnits' in figure.breakdown) keys.push('strategic.health.help.uncosted');
  if (ZONA_STOCK_MEASURES.includes(figure.measure) && 'articles' in figure.breakdown) keys.push('strategic.health.help.zonaStockParts');
  if (figure.uncovered !== null) keys.push('strategic.health.help.uncovered');
  if (figure.known !== null && figure.total !== null) {
    keys.push(report === 'markdown-exposure' && figure.measure === 'markdown_exposure_units' ? 'strategic.health.help.coverageArticles' : 'strategic.health.help.coverage');
  }
  keys.push(targetHelpKey(figure.target));
  keys.push('strategic.health.help.source');
  return keys;
}

/** The status a target gives a figure, drawn with its icon and words — or nothing with no target in force. */
export function RagStatus({ figure }: { readonly figure: HealthFigureModel }): React.JSX.Element | null {
  const { i18n } = useTranslation();
  const rag = ragOf(figure);
  if (rag === null || figure.target === null) return null;
  const target = formatFigure({ value: figure.target.value, unit: figure.unit, currencyCode: figure.currencyCode }, i18n.language) ?? '';
  return (
    <span data-rag={figure.target.rag ?? ''} className="text-[12px]">
      <StatusIndicator level={rag.level} labelKey={rag.labelKey} values={{ target }} />
    </span>
  );
}

/** One KPI: label and (i), the value, the line under it, the change from the previous period, the colour. */
export function HealthKpi({ figure, data, report, href }: {
  readonly figure: HealthFigureModel;
  readonly data: HealthReportModel;
  readonly report: HealthReportId;
  /** The list the figure counts, where there is one that totals it (ADR 9069). */
  readonly href?: string | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const words = measureWords(figure.measure);
  const value = formatFigure(figure, locale);
  const link = useKpiLink(value === null || href === null || href === undefined ? undefined : href);
  const n = (count: number): string => formatInteger(count, locale);
  const markdown = report === 'markdown-exposure' ? markdownKpiContext(figure, locale) : null;
  const context = figure.unit === 'share' && figure.numerator !== null && figure.denominator !== null
    ? t('strategic.health.kpi.ofBase', { numerator: n(figure.numerator), denominator: n(figure.denominator) })
    : markdown === null ? null : t(markdown.key, markdown.values);
  const polarity = polarityOf(figure.polarity);
  return (
    <div data-kpi={figure.measure} className={`flex min-w-0 flex-col gap-[2px] py-3 pe-3${link.rootClass === '' ? '' : ` ${link.rootClass}`}`}>
      <dt className="text-ink-secondary flex items-center gap-1 text-[11px] uppercase leading-4 tracking-[0.06em]">
        <span {...link.labelProps} className="min-w-0">{t(words.labelKey)}</span>
        <KpiLinkAbove link={link}>
          <AboutPopover aboutKeys={figureHelpKeys(figure, report)} title={t(words.labelKey)} size="sm" values={figureHelpValues(figure, data, locale)} />
        </KpiLinkAbove>
      </dt>
      <dd className="flex flex-col gap-1">
        {value === null ? (
          <>
            <span className="font-numeric text-ink-secondary text-[26px] font-semibold leading-[32px]">{t('template.kpi.absent')}</span>
            <span className="text-ink-secondary text-[11px] leading-4" data-kpi-absent={figure.absentReason ?? ''}>{absentWords(t, figure)}</span>
          </>
        ) : (
          <KpiLinkAnchor link={link} className="flex flex-col gap-1">
            <span {...link.valueProps} className="font-numeric text-ink-primary text-[26px] font-semibold leading-[32px] tabular-nums">{value}</span>
            {context !== null && <span className="text-ink-secondary text-[11px] leading-4 [overflow-wrap:anywhere]" data-kpi-context="">{context}</span>}
            {figure.uncovered !== null && figure.uncovered > 0 && (
              <span className="text-health-out-text text-[12px] font-semibold" data-kpi-uncovered="">
                {t('strategic.health.kpi.uncovered', { count: figure.uncovered, n: n(figure.uncovered) })}
              </span>
            )}
          </KpiLinkAnchor>
        )}
        {figure.change !== null && polarity !== 'none' && (
          <span className="flex flex-wrap items-center gap-1 text-[11px]" data-kpi-change="">
            <DeltaValue value={figure.change} polarity={polarity} kind={figure.unit === 'share' ? 'percent' : 'number'} comparisonLabelKey="strategic.health.kpi.changeAria" />
            <span className="text-ink-secondary">{t('strategic.health.kpi.vsPrevious')}</span>
          </span>
        )}
        <RagStatus figure={figure} />
      </dd>
    </div>
  );
}

/** At most four figures under a 2px ink rule (ADR 9032). */
export function HealthKpiRow({ data, report }: { readonly data: HealthReportModel; readonly report: HealthReportId }): React.JSX.Element | null {
  const figures = data.snapshot.slice(0, 4);
  if (figures.length === 0) return null;
  return (
    <dl data-template-kpis="" data-layout-block="" data-tour="kpi-row" className="border-ink-primary grid grid-cols-2 border-t-2 lg:grid-cols-4">
      {figures.map((figure) => <HealthKpi key={figure.measure} figure={figure} data={data} report={report} />)}
    </dl>
  );
}
